import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import type MarketSnapshotsBaseController from 'lumen/controllers/market/snapshots/base';
import type { Kpi } from 'lumen/models/market-performance/data-item';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import type { Transition } from 'lumen/services/transition';
import {
  type DataPreferences,
  GROUP_BY,
  parseDataPreferences,
  parseTableLayoutPreferences,
  type SnapshotViewConfig,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import type { ColumnSettings } from 'lumen/utils/market-snapshots/column-settings';
import {
  DEFAULT_PAGE_SIZE,
  getDefaultDateRange,
} from 'lumen/utils/market-snapshots/defaults';
import {
  buildApiOrderBy,
  getEffectiveOrderBy,
} from 'lumen/utils/market-snapshots/order-by';
import {
  fetchSnapshotItems,
  type MarketSnapshotsParams,
  type SnapshotItems,
  type SnapshotItemsParams,
  type SnapshotRows,
} from 'lumen/utils/market-snapshots/query';
import {
  readColumnSettings,
  readStored,
} from 'lumen/utils/market-snapshots/storage';

type LoadDataItemsParams = Omit<SnapshotItemsParams, 'marketId'>;

export type MarketSnapshotsModel = {
  loadDataItemsTask: Task<SnapshotItems, [LoadDataItemsParams]>;
};

/** A view's stored preferences. */
export interface StoredPreferences {
  columns: { kpiSettings: ColumnSettings<Kpi>; settings: ColumnSettings };
  data: DataPreferences;
  tableLayout: TableLayoutPreferences;
}

/**
 * Shared query params, validation, fetching and controller setup for the market
 * snapshots views; what differs per view comes from `config`.
 * Not decorated: each concrete view route applies `@protectedRoute`
 * (the decorator subclasses the class it wraps, see its unit test).
 */
export default abstract class MarketSnapshotsBaseRoute extends Route {
  queryParams = {
    fromDate: { refreshModel: true },
    toDate: { refreshModel: true },
    groupBy: { refreshModel: true },
    orderBy: { refreshModel: true },
    page: { refreshModel: true },
    pageSize: { refreshModel: true },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service('local-storage') declare localStorage: Services['local-storage'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  abstract readonly config: SnapshotViewConfig;

  loadDataItemsTask = task(
    { restartable: true },
    async (params: LoadDataItemsParams) =>
      fetchSnapshotItems(
        {
          alerting: this.alerting,
          errorTracking: this.errorTracking,
          store: this.store,
        },
        { ...params, marketId: this.hotelAndMarketService.market.id! },
      ),
  );

  beforeModel(transition: Transition): void {
    this.transition.validateMarketSnapshotsQueryParams(
      transition,
      this.paramsFor(this.routeName),
      this.routeName,
    );
  }

  /** A bookmarked or stale sort on a column the stored layout hides (or on an unsortable view) falls back to `name`. */
  effectiveOrderBy(orderBy: string, stored: StoredPreferences): string {
    if (!this.config.isSortable) {
      return 'name';
    }

    return getEffectiveOrderBy(orderBy, {
      columnSettings: stored.columns.settings,
      fixed: this.config.columns.fixed,
      kpiSettings: stored.columns.kpiSettings,
      layout: stored.tableLayout,
      metrics: this.config.columns.metrics(stored.data),
    });
  }

  model(params: MarketSnapshotsParams): MarketSnapshotsModel {
    const defaultDateRange = getDefaultDateRange(
      this.hotelAndMarketService.getLocalTime(),
    );
    const stored = this.readStoredPreferences();

    const groupBy = params.groupBy ?? GROUP_BY.PROPERTY;
    const rows: SnapshotRows =
      groupBy === GROUP_BY.MONTH
        ? { groupBy }
        : {
            groupBy,
            // The URL keeps the semantic sort; the API form is derived only here.
            orderBy: buildApiOrderBy(
              this.effectiveOrderBy(params.orderBy ?? 'name', stored),
              stored.tableLayout.sortOn,
              stored.data.variance,
            ),
            pagination: {
              page: params.page ?? 1,
              pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
            },
          };

    void this.loadDataItemsTask.perform({
      compare: this.config.api.compare(stored.data),
      view: this.config.api.view,
      ...(this.config.api.pickup && { pickup: this.config.api.pickup }),
      ...(this.config.api.segments && { segments: true }),
      dateRange: {
        from: params.fromDate ?? defaultDateRange.fromDate,
        to: params.toDate ?? defaultDateRange.toDate,
      },
      rows,
    });

    return {
      loadDataItemsTask: this.loadDataItemsTask,
    };
  }

  /** Reads every stored preference of this view once. */
  readStoredPreferences(): StoredPreferences {
    const { columns } = this.config;

    // Data and table-layout preferences are shared by every view (columns stay per view).
    return {
      data: readStored(
        this.localStorage,
        LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_DATA_PREFERENCES,
        parseDataPreferences,
      ),
      tableLayout: readStored(
        this.localStorage,
        LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TABLE_LAYOUT,
        parseTableLayoutPreferences,
      ),
      columns: {
        // KPI sets never have a fixed column, so one KPI always stays visible.
        kpiSettings: readColumnSettings(
          this.localStorage,
          columns.storageKeys.kpiColumns,
          columns.kpis,
        ),
        settings: readColumnSettings(
          this.localStorage,
          columns.storageKeys.columns,
          columns.ids,
          columns.fixed,
        ),
      },
    };
  }

  setupController(
    controller: MarketSnapshotsBaseController,
    model: MarketSnapshotsModel,
  ): void {
    super.setupController(controller, model);

    const stored = this.readStoredPreferences();

    controller.columnSettings = stored.columns.settings;
    controller.dataPreferences = stored.data;
    controller.kpiColumnSettings = stored.columns.kpiSettings;
    controller.tableLayoutPreferences = stored.tableLayout;

    // Keep the URL QP in line with what model() fetched.
    const orderBy = this.effectiveOrderBy(controller.orderBy, stored);

    if (orderBy !== controller.orderBy) {
      controller.orderBy = orderBy;
    }
  }
}
